import { ApiEnvelope, UserEntity } from "@/types/canonical";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosPrivateClient from "@/api/base/private/axiosPrivateClient";

interface AuthState {
  user: UserEntity | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
};

function unwrapEnvelope<T>(res: T | ApiEnvelope<T>): T {
  const maybe = res as ApiEnvelope<T>;
  return maybe && typeof maybe === "object" && "entity" in maybe
    ? (maybe.entity as T)
    : (res as T);
}

export const checkAuth = createAsyncThunk<UserEntity | null>(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      // Check localStorage for access token instead of cookies
      const token = typeof window !== "undefined"
        ? localStorage.getItem("access_token")
        : null;
      
      if (!token) return rejectWithValue("no_token");

      const res = await axiosPrivateClient.get<ApiEnvelope<UserEntity> | UserEntity>(
        "/user.json?extended=1"
      );

      const entity = unwrapEnvelope<UserEntity>(res.data);

      if (!entity) return rejectWithValue("no_entity");
      return entity;
    } catch {
      return rejectWithValue("error");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserEntity | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      // Cookie cleanup happens via `/api/auth/logout`.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("auth-token-changed"));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.user = action.payload; // UserEntity | null
        state.isAuthenticated = !!action.payload; // true если есть entity
        state.isLoading = false;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      });
  },
});

export const { setUser, logout } = authSlice.actions;
export default authSlice.reducer;
